import { useEffect, useState, useMemo } from 'react';
import { SaveOutlined, SearchOutlined, AppstoreOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { Button, Col, Form, Input, InputNumber, Row, Space, Tag, Select, Modal, Table, Card, Typography, Tooltip } from 'antd';
import { toast } from "react-toastify";
import { maChucVu, responseStatus } from "../../constants";
import { validateMessages } from "../../helpers/common";
import { DeXuatNhuCauTaiSan, DeXuatNhuCauTaiSanRequest, TrangThaiDeXuatNhuCau, LoaiNhuCauTaiSan, PhanLoaiTaiSanNhuCau } from "../../models/taisan/DeXuatNhuCauTaiSan";
import { TaiSan, TrangThaiTaiSan } from "../../models/taisan/TaiSan";
import { luuDeXuat, chuyenCVVPDeXuat } from "../../services/deXuatNhuCauTaiSan";
import { layDanhSachTaiSanCoTheBanGiao } from "../../services/bienBanBanGiao";
import { layDanhSachTaiSanCoTheThanhLy } from "../../services/thanhlytaisan";
import { useSelector } from 'react-redux';

const { TextArea } = Input;
const { Text } = Typography;

const LuuDeXuatNhuCauBox = ({ modelProps, onSubmit }: {
    modelProps: DeXuatNhuCauTaiSan | null,
    onSubmit: () => void
}) => {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [form] = Form.useForm();
    const loaiNhuCauWatch = Form.useWatch('loaiNhuCau', form);

    const user = useSelector((state: any) => state.auth.currentUser);
    const isThemMoi = !modelProps?.id;
    const isCreator = isThemMoi || modelProps?.idNVKhoiTao === user?.id || modelProps?.idNVKhoiTao === user?.idNhanVien;

    // Creator chi duoc sua khi trang thai la: Khoi tao, Thu hoi, LD Phong tra lai
    const creatorEditableStatuses = [
        TrangThaiDeXuatNhuCau.KhoiTao,
        TrangThaiDeXuatNhuCau.ThuHoi,
        TrangThaiDeXuatNhuCau.LanhDaoPhongTraLai,
    ];
    const isChanhVP = user?.maChucVu === maChucVu.chanhVP;
    const isBuocChanhVP = !!modelProps && [TrangThaiDeXuatNhuCau.ChuyenChanhVP].includes(modelProps.trangThai);
    const canCreatorEdit = isCreator && (isThemMoi || (modelProps != null && creatorEditableStatuses.includes(modelProps.trangThai)));
    const canChanhVPEdit = isChanhVP && isBuocChanhVP;
    const isReadOnly = !(canCreatorEdit || canChanhVPEdit);

    // Modal chọn tài sản
    const [dsTaiSan, setDsTaiSan] = useState<TaiSan[]>([]);
    const [loadingTaiSan, setLoadingTaiSan] = useState<boolean>(false);
    const [modalTaiSanOpen, setModalTaiSanOpen] = useState<boolean>(false);
    const [tuKhoaTaiSan, setTuKhoaTaiSan] = useState<string>('');
    const [taiSanDuocChon, setTaiSanDuocChon] = useState<TaiSan | null>(null);

    const getTrangThaiInfo = (value?: number) => {
        switch (value) {
            case TrangThaiDeXuatNhuCau.KhoiTao: return { label: 'Khởi tạo', color: 'default' };
            case TrangThaiDeXuatNhuCau.ThuHoi: return { label: 'Thu hồi', color: 'red' };
            case TrangThaiDeXuatNhuCau.TrinhLanhDaoPhong: return { label: 'Trình LĐ Phòng', color: 'blue' };
            case TrangThaiDeXuatNhuCau.LanhDaoPhongTraLai: return { label: 'LĐ Phòng trả lại', color: 'orange' };
            case TrangThaiDeXuatNhuCau.ChuyenChanhVP: return { label: 'Chuyển Chánh VP', color: 'blue' };
            case TrangThaiDeXuatNhuCau.ChanhVPTraLai: return { label: 'Chánh VP trả lại', color: 'orange' };
            case TrangThaiDeXuatNhuCau.CVVPThamMuu: return { label: 'CV VP tham mưu', color: 'cyan' };
            case TrangThaiDeXuatNhuCau.CVVPTraLai: return { label: 'CV VP trả lại', color: 'orange' };
            case TrangThaiDeXuatNhuCau.TrinhPhoCVP: return { label: 'Trình Phó CVP', color: 'blue' };
            case TrangThaiDeXuatNhuCau.PhoCVPTraLai: return { label: 'Phó CVP trả lại', color: 'orange' };
            case TrangThaiDeXuatNhuCau.TrinhCVP: return { label: 'Trình CVP', color: 'blue' };
            case TrangThaiDeXuatNhuCau.CVPTraLai: return { label: 'CVP trả lại', color: 'orange' };
            case TrangThaiDeXuatNhuCau.TrinhPhoGiamDoc: return { label: 'Trình Phó GĐ', color: 'blue' };
            case TrangThaiDeXuatNhuCau.PhoGiamDocTraLai: return { label: 'Phó GĐ trả lại', color: 'orange' };
            case TrangThaiDeXuatNhuCau.TrinhGiamDoc: return { label: 'Trình GĐ', color: 'blue' };
            case TrangThaiDeXuatNhuCau.GiamDocTraLai: return { label: 'GĐ trả lại', color: 'orange' };
            case TrangThaiDeXuatNhuCau.PheDuyet: return { label: 'Đã phê duyệt', color: 'green' };
            default: return { label: 'Không xác định', color: 'default' };
        }
    };

    const getTrangThaiTaiSanText = (status?: number) => {
        switch (status) {
            case TrangThaiTaiSan.MoiNhap: return "Mới nhập";
            case TrangThaiTaiSan.DangSuDung: return "Đang sử dụng";
            case TrangThaiTaiSan.BaoTri: return "Bảo trì";
            case TrangThaiTaiSan.Hong: return "Hỏng";
            case TrangThaiTaiSan.ChoThanhLy: return "Chờ thanh lý";
            case TrangThaiTaiSan.DieuChuyen: return "Điều chuyển";
            case TrangThaiTaiSan.DaThanhLy: return "Đã thanh lý";
            default: return "Đang sử dụng";
        }
    };

    // Load danh sách tài sản của phòng mình
    const loadDanhSachTaiSan = async () => {
        try {
            setLoadingTaiSan(true);
            let result: TaiSan[] = [];
            if (user?.idDonVi) {
                result = await layDanhSachTaiSanCoTheBanGiao(user.idDonVi);
            }
            if (!result || result.length === 0) {
                result = await layDanhSachTaiSanCoTheThanhLy();
                if (user?.idDonVi) {
                    result = result.filter(x => x.idDonVi === user.idDonVi);
                }
            }
            setDsTaiSan(result || []);
        } catch (error: any) {
            console.error("Lỗi khi tải danh sách tài sản phòng:", error);
        } finally {
            setLoadingTaiSan(false);
        }
    };

    useEffect(() => {
        loadDanhSachTaiSan();
    }, [user?.idDonVi]);

    const setFormValues = (value: DeXuatNhuCauTaiSan | null) => {
        form.setFieldsValue({
            id: value?.id,
            loaiNhuCau: value?.loaiNhuCau ?? LoaiNhuCauTaiSan.MuaMoi,
            idTaiSan: value?.idTaiSan,
            tenTaiSan: value?.tenTaiSan,
            soLuong: value?.soLuong ?? 1,
            donViTinh: value?.donViTinh,
            tinhTrangTaiSan: value?.tinhTrangTaiSan,
            phanLoaiTaiSan: value?.phanLoaiTaiSan ?? PhanLoaiTaiSanNhuCau.TSCD,
            moTa: value?.moTa,
            trangThai: value?.trangThai ?? TrangThaiDeXuatNhuCau.KhoiTao,
        });

        if (value?.idTaiSan) {
            const found = dsTaiSan.find(x => x.id === value.idTaiSan);
            if (found) {
                setTaiSanDuocChon(found);
            } else {
                setTaiSanDuocChon({
                    id: value.idTaiSan,
                    ma: value.maTaiSan || "",
                    tenTaiSan: value.tenTaiSan || "",
                    soLuong: value.soLuong || 1,
                    idLoaiTaiSan: 0,
                    idDonVi: value.idDonVi || 0,
                    trangThai: 0
                });
            }
        } else {
            setTaiSanDuocChon(null);
        }
    };

    useEffect(() => {
        setFormValues(modelProps);
    }, [modelProps, dsTaiSan]);

    // Handle chọn tài sản từ danh sách
    const handleChonTaiSan = (ts: TaiSan) => {
        setTaiSanDuocChon(ts);
        const loaiStr = (ts.loaiTaiSan || "").toLowerCase();
        const phanLoai = (loaiStr.includes("công cụ") || loaiStr.includes("ccdc")) 
            ? PhanLoaiTaiSanNhuCau.CCDC 
            : PhanLoaiTaiSanNhuCau.TSCD;

        form.setFieldsValue({
            idTaiSan: ts.id,
            tenTaiSan: ts.tenTaiSan,
            soLuong: ts.soLuong && ts.soLuong > 0 ? ts.soLuong : 1,
            donViTinh: form.getFieldValue("donViTinh") || "Cái",
            tinhTrangTaiSan: getTrangThaiTaiSanText(ts.trangThai),
            phanLoaiTaiSan: phanLoai,
        });
        setModalTaiSanOpen(false);
    };

    const handleHuyChonTaiSan = () => {
        setTaiSanDuocChon(null);
        form.setFieldsValue({
            idTaiSan: undefined,
            tenTaiSan: '',
            soLuong: 1,
            tinhTrangTaiSan: '',
        });
    };

    const onFinish = async (values: any) => {
        setIsLoading(true);
        try {
            if (canChanhVPEdit && modelProps?.trangThai === TrangThaiDeXuatNhuCau.ChuyenChanhVP) {
                const response = await chuyenCVVPDeXuat(modelProps.id!);
                if (response.trangThai === responseStatus.thanhCong) {
                    toast.success("Chuyển CV VP tham mưu thành công");
                    onSubmit();
                } else {
                    toast.error(response.thongBao || "Chuyển không thành công");
                }
            } else {
                const isMuaMoi = values.loaiNhuCau === LoaiNhuCauTaiSan.MuaMoi;
                if (!isMuaMoi && !values.idTaiSan) {
                    toast.warning("Vui lòng chọn tài sản từ phòng của bạn");
                    setIsLoading(false);
                    return;
                }

                const payload: DeXuatNhuCauTaiSanRequest = {
                    id: values.id,
                    idLanhDaoPhong: modelProps?.idLanhDaoPhong || user?.idLanhDaoPhong || 0,
                    loaiNhuCau: values.loaiNhuCau,
                    idTaiSan: isMuaMoi ? undefined : values.idTaiSan,
                    tenTaiSan: values.tenTaiSan,
                    soLuong: values.soLuong || 1,
                    donViTinh: values.donViTinh,
                    tinhTrangTaiSan: values.tinhTrangTaiSan,
                    phanLoaiTaiSan: values.phanLoaiTaiSan,
                    moTa: values.moTa,
                    noiDungThamMuu: modelProps?.noiDungThamMuu,
                    idPhoCVP: modelProps?.idPhoCVP || 0,
                    idCVP: modelProps?.idCVP || 0,
                    idPhoGiamDoc: modelProps?.idPhoGiamDoc || 0,
                    idGiamDoc: modelProps?.idGiamDoc || 0,
                };
                const response = await luuDeXuat(payload);
                if (response.trangThai === responseStatus.thanhCong) {
                    toast.success("Lưu thành công");
                    onSubmit();
                } else {
                    toast.error(response.thongBao || "Lưu không thành công");
                }
            }
        } catch (error: any) {
            toast.error(error.message || "Lỗi khi lưu");
        } finally {
            setIsLoading(false);
        }
    };

    const dsTaiSanLoc = useMemo(() => {
        if (!tuKhoaTaiSan.trim()) return dsTaiSan;
        const kw = tuKhoaTaiSan.trim().toLowerCase();
        return dsTaiSan.filter(ts => 
            (ts.ma || "").toLowerCase().includes(kw) ||
            (ts.tenTaiSan || "").toLowerCase().includes(kw) ||
            (ts.nguoiSuDung || "").toLowerCase().includes(kw) ||
            (ts.loaiTaiSan || "").toLowerCase().includes(kw)
        );
    }, [dsTaiSan, tuKhoaTaiSan]);

    const columnsTaiSan = [
        {
            title: "Mã tài sản",
            dataIndex: "ma",
            key: "ma",
            width: 140,
            render: (text: string) => <Text strong>{text || "-"}</Text>
        },
        {
            title: "Tên tài sản",
            dataIndex: "tenTaiSan",
            key: "tenTaiSan",
            render: (text: string, record: TaiSan) => (
                <div>
                    <div><Text strong>{text}</Text></div>
                    {record.loaiTaiSan && <div style={{ fontSize: 12, color: "#8c8c8c" }}>{record.loaiTaiSan}</div>}
                </div>
            )
        },
        {
            title: "Người sử dụng",
            dataIndex: "nguoiSuDung",
            key: "nguoiSuDung",
            width: 160,
            render: (text: string) => text || "-"
        },
        {
            title: "Số lượng",
            dataIndex: "soLuong",
            key: "soLuong",
            width: 90,
            align: "center" as const,
            render: (val: number) => val || 1
        },
        {
            title: "Trạng thái",
            dataIndex: "trangThai",
            key: "trangThai",
            width: 130,
            render: (status: number) => <Tag color="blue">{getTrangThaiTaiSanText(status)}</Tag>
        },
        {
            title: "Thao tác",
            key: "action",
            width: 100,
            align: "center" as const,
            render: (_: any, record: TaiSan) => (
                <Button 
                    type="primary" 
                    size="small" 
                    icon={<CheckCircleOutlined />}
                    onClick={() => handleChonTaiSan(record)}
                >
                    Chọn
                </Button>
            )
        }
    ];

    const isMuaMoi = (loaiNhuCauWatch ?? modelProps?.loaiNhuCau ?? LoaiNhuCauTaiSan.MuaMoi) === LoaiNhuCauTaiSan.MuaMoi;

    return (
        <Form form={form} layout="vertical" onFinish={onFinish} validateMessages={validateMessages} disabled={isReadOnly}>
            <Form.Item name="id" hidden><Input /></Form.Item>
            <Form.Item name="idTaiSan" hidden><Input /></Form.Item>

            <Row gutter={[16, 16]}>
                <Col xs={24} sm={24} md={12}>
                    <Form.Item name="loaiNhuCau" label="Loại nhu cầu *" rules={[{ required: true }]}>
                        <Select 
                            placeholder="Chọn loại nhu cầu" 
                            onChange={(val) => {
                                if (val === LoaiNhuCauTaiSan.MuaMoi) {
                                    handleHuyChonTaiSan();
                                }
                            }}
                            options={[
                                { label: 'Mua mới', value: LoaiNhuCauTaiSan.MuaMoi },
                                //{ label: 'Bàn giao', value: LoaiNhuCauTaiSan.BanGiao },
                                { label: 'Sửa chữa', value: LoaiNhuCauTaiSan.SuaChua },
                                //{ label: 'Điều chuyển', value: LoaiNhuCauTaiSan.DieuChuyen }
                            ]} 
                        />
                    </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={12}>
                    <Form.Item label="Trạng thái">
                        <Tag color={getTrangThaiInfo(modelProps?.trangThai).color} style={{ fontSize: 13, padding: '4px 10px' }}>
                            {getTrangThaiInfo(modelProps?.trangThai).label}
                        </Tag>
                    </Form.Item>
                </Col>
            </Row>

            {/* Nếu không phải Mua mới -> Bắt buộc chọn tài sản thuộc phòng mình */}
            {!isMuaMoi && (
                <div style={{ marginBottom: 20 }}>
                    <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <label style={{ fontWeight: 500 }}>Tài sản thuộc phòng đề xuất *</label>
                        {!isReadOnly && (
                            <Button 
                                type="primary" 
                                ghost 
                                icon={<AppstoreOutlined />} 
                                onClick={() => {
                                    setTuKhoaTaiSan('');
                                    setModalTaiSanOpen(true);
                                }}
                            >
                                {taiSanDuocChon ? "Chọn tài sản khác" : "Chọn tài sản"}
                            </Button>
                        )}
                    </div>

                    {taiSanDuocChon ? (
                        <Card size="small" style={{ background: '#f6ffed', borderColor: '#b7eb8f' }}>
                            <Row gutter={[16, 8]} align="middle">
                                <Col xs={24} sm={12} md={6}>
                                    <Text type="secondary">Mã tài sản:</Text> <div><Text strong>{taiSanDuocChon.ma || "-"}</Text></div>
                                </Col>
                                <Col xs={24} sm={12} md={8}>
                                    <Text type="secondary">Tên tài sản:</Text> <div><Text strong>{taiSanDuocChon.tenTaiSan}</Text></div>
                                </Col>
                                <Col xs={24} sm={12} md={5}>
                                    <Text type="secondary">Người sử dụng:</Text> <div>{taiSanDuocChon.nguoiSuDung || "-"}</div>
                                </Col>
                                <Col xs={24} sm={12} md={5} style={{ textAlign: 'right' }}>
                                    {!isReadOnly && (
                                        <Tooltip title="Bỏ chọn">
                                            <Button 
                                                danger 
                                                type="text" 
                                                icon={<CloseCircleOutlined />} 
                                                onClick={handleHuyChonTaiSan}
                                            >
                                                Bỏ chọn
                                            </Button>
                                        </Tooltip>
                                    )}
                                </Col>
                            </Row>
                        </Card>
                    ) : (
                        <div style={{ 
                            padding: '16px', 
                            border: '1px dashed #d9d9d9', 
                            borderRadius: '6px', 
                            textAlign: 'center', 
                            background: '#fafafa',
                            color: '#8c8c8c' 
                        }}>
                            Chưa chọn tài sản. Nhấn nút <b>"Chọn tài sản"</b> để chọn tài sản thuộc phòng của bạn.
                        </div>
                    )}
                </div>
            )}

            <Row gutter={[16, 16]}>
                <Col xs={24} sm={24} md={12}>
                    <Form.Item 
                        name="tenTaiSan" 
                        label="Tên tài sản *" 
                        rules={[{ required: true, message: 'Vui lòng nhập tên tài sản' }]}
                    >
                        <Input placeholder="Nhập tên tài sản" />
                    </Form.Item>
                </Col>

                <Col xs={24} sm={12} md={6}>
                    <Form.Item 
                        name="phanLoaiTaiSan" 
                        label="Loại tài sản: CCDC / TSCĐ *" 
                        rules={[{ required: true, message: 'Vui lòng chọn loại tài sản' }]}
                    >
                        <Select 
                            placeholder="Chọn phân loại"
                            options={[
                                { label: 'Tài sản cố định (TSCĐ)', value: PhanLoaiTaiSanNhuCau.TSCD },
                                { label: 'Công cụ dụng cụ (CCDC)', value: PhanLoaiTaiSanNhuCau.CCDC },
                            ]}
                        />
                    </Form.Item>
                </Col>

                <Col xs={12} sm={6} md={3}>
                    <Form.Item 
                        name="soLuong" 
                        label="Số lượng *" 
                        rules={[{ required: true, message: 'Nhập số lượng' }]}
                    >
                        <InputNumber min={1} style={{ width: '100%' }} placeholder="SL" />
                    </Form.Item>
                </Col>

                <Col xs={12} sm={6} md={3}>
                    <Form.Item 
                        name="donViTinh" 
                        label="ĐVT"
                    >
                        <Input placeholder="Cái, Bộ..." />
                    </Form.Item>
                </Col>
            </Row>

            {!isMuaMoi && (
                <Row gutter={[16, 16]}>
                    <Col xs={24} sm={24} md={12}>
                        <Form.Item 
                            name="tinhTrangTaiSan" 
                            label="Tình trạng tài sản"
                        >
                            <Input placeholder="VD: Đang sử dụng, Hỏng màn hình, Cần bảo trì..." />
                        </Form.Item>
                    </Col>
                </Row>
            )}

            <Form.Item 
                name="moTa" 
                label="Ghi chú"
            >
                <TextArea rows={4} placeholder={isMuaMoi ? "Nhập ghi chú chi tiết tài sản cần mua mới, lý do và mục đích sử dụng..." : "Nhập chi tiết nhu cầu bàn giao / hiện trạng hư hỏng cần sửa chữa / lý do điều chuyển..."} />
            </Form.Item>

            <Form.Item>
                <Space>
                    <Button type="primary" htmlType="submit" loading={isLoading} icon={<SaveOutlined />}>
                        {canChanhVPEdit ? "Chuyển CV VP" : "Lưu"}
                    </Button>
                </Space>
            </Form.Item>

            {/* Modal chọn tài sản phòng */}
            <Modal
                title="Chọn tài sản thuộc đơn vị / phòng ban"
                open={modalTaiSanOpen}
                onCancel={() => setModalTaiSanOpen(false)}
                footer={[
                    <Button key="close" onClick={() => setModalTaiSanOpen(false)}>
                        Đóng
                    </Button>
                ]}
                width={850}
            >
                <div style={{ marginBottom: 16 }}>
                    <Input 
                        prefix={<SearchOutlined />} 
                        placeholder="Tìm kiếm theo mã, tên tài sản, người sử dụng..." 
                        value={tuKhoaTaiSan}
                        onChange={(e) => setTuKhoaTaiSan(e.target.value)}
                        allowClear
                    />
                </div>
                <Table 
                    columns={columnsTaiSan}
                    dataSource={dsTaiSanLoc}
                    rowKey="id"
                    loading={loadingTaiSan}
                    pagination={{ pageSize: 6, showTotal: (total) => `Tổng số: ${total} tài sản` }}
                    size="small"
                />
            </Modal>
        </Form>
    );
};

export default LuuDeXuatNhuCauBox;
